/**
 * Board Listing Editor — a hardware provider's public posting of one board
 * (marketplace stage 1b): the shared board page (headline, description,
 * board-level specs), the products (SKUs) built on it — each with its own
 * headline, description, spec rows and hero — and the images and documents,
 * each attached to the whole board or to one product.
 *
 * Route: /hardware-providers/:id/boards/:boardId/listing. Super admins and
 * the board's manufacturer admin can edit; the API decides, this page just
 * surfaces the errors. Board/product text saves explicitly; files save as
 * you go.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { IconArrowUp, IconArrowDown, IconTrash, IconStar, IconStarFilled, IconExternalLink, IconPlus } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin, isManufacturerAdmin } from '../services/users';
import { getDeviceBoard, boardLabel } from '../services/deviceBoards';
import {
  getListing, updateListing, publishListing,
  createVariant, updateVariant, reorderVariants, deleteVariant,
  uploadAsset, updateAsset, reorderAssets, deleteAsset,
  publicListingPath, formatBytes, IMAGE_ACCEPT, DOCUMENT_ACCEPT, VARIANT_STATUS_LABELS,
} from '../services/listings';
import type { BoardListing, BoardVariant, BoardAsset, SpecRow, VariantInput, VariantStatus } from '../services/listings';
import { getManufacturer } from '../services/manufacturers';
import { AssetImage } from '../components/AssetImage';
import { SegmentedTabs } from '../components/SegmentedTabs';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  Button,
  StatusChip,
  LoadingText,
  EmptyState,
  MutedText,
  Caption,
  Row,
  Stack,
  DetailCard,
  SectionTitle,
  FormGroup,
  Label,
  Input,
  Select,
  TextArea,
  HelperText,
  ErrorAlert,
  TableContainer,
  StyledTable,
  Th,
  Td,
  IconButton,
} from '../components/styled';

const BackLink = styled(Link)`
  color: ${({ theme }) => theme.font.color.secondary};
  text-decoration: none;
  font-size: ${({ theme }) => theme.font.size.sm};
  &:hover { color: ${({ theme }) => theme.accent.primary}; }
`;

const PublicLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${({ theme }) => theme.spacing(2, 3)};
  border: 1px solid ${({ theme }) => theme.border.color.strong};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  color: ${({ theme }) => theme.font.color.primary};
  text-decoration: none;
  font-size: ${({ theme }) => theme.font.size.sm};
  &:hover { border-color: ${({ theme }) => theme.accent.primary}; color: ${({ theme }) => theme.accent.primary}; }
`;

const SpacedSectionTitle = styled(SectionTitle)`
  margin-top: ${({ theme }) => theme.spacing(8)};
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 ${({ theme }) => theme.spacing(3)};
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Tile = styled.div<{ hero?: boolean }>`
  border: 2px solid ${({ theme, hero }) => (hero ? theme.accent.primary : theme.border.color.light)};
  border-radius: ${({ theme }) => theme.border.radius.md};
  padding: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.background.primary};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};

  img {
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: contain;
    background: ${({ theme }) => theme.background.secondary};
    border-radius: ${({ theme }) => theme.border.radius.sm};
  }
`;

const TileActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  align-items: center;
  justify-content: space-between;
`;

const SpecTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td { text-align: left; padding: ${({ theme }) => theme.spacing(1)}; vertical-align: top; }
  th { color: ${({ theme }) => theme.font.color.tertiary}; font-weight: ${({ theme }) => theme.font.weight.medium}; font-size: ${({ theme }) => theme.font.size.sm}; }
  input { width: 100%; }
`;

const StatusBar = styled(DetailCard)<{ live: boolean }>`
  border-left: 4px solid ${({ theme, live }) => (live ? theme.color.green : theme.border.color.strong)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const HiddenFile = styled.input`
  display: none;
`;

const GroupHeading = styled.h3`
  font-size: ${({ theme }) => theme.font.size.md};
  margin: ${({ theme }) => theme.spacing(5)} 0 ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.font.color.primary};
`;

type Tab = 'board' | 'products' | 'images' | 'documents';

const emptySpec = (): SpecRow => ({ group: '', label: '', value: '' });

function cleanSpecs(specs: SpecRow[]): SpecRow[] {
  return specs
    .filter((s) => s.label.trim())
    .map((s) => ({ group: s.group?.trim() || null, label: s.label.trim(), value: s.value.trim() }));
}

function errorText(err: any, fallback: string): string {
  const body = err?.response?.data;
  return body?.details?.join?.('; ') || body?.error || fallback;
}

/* ── reusable spec table editor ── */

function SpecEditor({ specs, onChange }: { specs: SpecRow[]; onChange: (s: SpecRow[]) => void }) {
  const update = (i: number, patch: Partial<SpecRow>) => onChange(specs.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const move = (i: number, delta: number) => {
    const t = i + delta;
    if (t < 0 || t >= specs.length) return;
    const next = [...specs];
    [next[i], next[t]] = [next[t], next[i]];
    onChange(next);
  };
  return (
    <>
      <SpecTable>
        <thead>
          <tr><th style={{ width: '22%' }}>Group</th><th style={{ width: '28%' }}>Label</th><th>Value</th><th style={{ width: 96 }}></th></tr>
        </thead>
        <tbody>
          {specs.map((s, i) => (
            <tr key={i}>
              <td><Input value={s.group || ''} onChange={(e) => update(i, { group: e.target.value })} placeholder="Connectivity" /></td>
              <td><Input value={s.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="Radio" /></td>
              <td><Input value={s.value} onChange={(e) => update(i, { value: e.target.value })} placeholder="LTE-M / NB-IoT" /></td>
              <td>
                <Row gap={1}>
                  <IconButton type="button" onClick={() => move(i, -1)} title="Move up"><IconArrowUp size={14} /></IconButton>
                  <IconButton type="button" onClick={() => move(i, 1)} title="Move down"><IconArrowDown size={14} /></IconButton>
                  <IconButton type="button" onClick={() => onChange(specs.filter((_, idx) => idx !== i))} title="Remove"><IconTrash size={14} /></IconButton>
                </Row>
              </td>
            </tr>
          ))}
        </tbody>
      </SpecTable>
      <Row gap={2} style={{ marginTop: 8 }}>
        <Button type="button" size="sm" variant="secondary" onClick={() => onChange([...specs, emptySpec()])}>
          <IconPlus size={14} /> Add row
        </Button>
      </Row>
    </>
  );
}

/* ── board text form ── */

interface BoardForm { slug: string; headline: string; description: string; specs: SpecRow[] }

const boardFormFrom = (listing: BoardListing | null, fallbackSlug: string): BoardForm => ({
  slug: listing?.slug || fallbackSlug,
  headline: listing?.headline || '',
  description: listing?.description || '',
  specs: (listing?.specs || []).map((s) => ({ group: s.group || '', label: s.label, value: s.value })),
});

/* ── product form ── */

interface ProductForm { name: string; sku: string; slug: string; status: VariantStatus; headline: string; description: string; specs: SpecRow[] }

const emptyProduct = (): ProductForm => ({ name: '', sku: '', slug: '', status: 'available', headline: '', description: '', specs: [] });
const productFormFrom = (v: BoardVariant): ProductForm => ({
  name: v.name, sku: v.sku, slug: v.slug, status: v.status,
  headline: v.headline || '', description: v.description || '',
  specs: (v.specs || []).map((s) => ({ group: s.group || '', label: s.label, value: s.value })),
});

function BoardListingEditor() {
  const { id, boardId: boardIdParam } = useParams();
  const manufacturerId = Number(id);
  const boardId = Number(boardIdParam);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('board');
  const [form, setForm] = useState<BoardForm | null>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const documentInput = useRef<HTMLInputElement>(null);
  const [uploadTarget, setUploadTarget] = useState<string>(''); // '' = shared, else variant id
  const [uploading, setUploading] = useState(false);
  const [productForm, setProductForm] = useState<ProductForm | null>(null);
  const [editingProduct, setEditingProduct] = useState<BoardVariant | null>(null);

  const userIsSuperAdmin = isSuperAdmin(user);
  const userIsManufacturerAdmin = isManufacturerAdmin(user);

  if (!userIsSuperAdmin && !userIsManufacturerAdmin) return <Navigate to="/dashboard" replace />;
  if (userIsManufacturerAdmin && user?.manufacturer_id != null && user.manufacturer_id !== manufacturerId) {
    return <Navigate to={`/hardware-providers/${user.manufacturer_id}`} replace />;
  }

  const { data: manufacturer } = useQuery({
    queryKey: ['manufacturer', manufacturerId],
    queryFn: () => getManufacturer(manufacturerId),
    enabled: Number.isFinite(manufacturerId),
  });
  const { data: board, isLoading: boardLoading } = useQuery({
    queryKey: ['device-board', boardId],
    queryFn: () => getDeviceBoard(boardId),
    enabled: Number.isFinite(boardId),
  });
  const { data, isLoading, isError } = useQuery({
    queryKey: ['listing', boardId],
    queryFn: () => getListing(boardId),
    enabled: Number.isFinite(boardId),
  });

  const listing = data?.listing ?? null;
  const variants = data?.variants ?? [];
  const assets = data?.assets ?? [];
  const images = assets.filter((a) => a.kind === 'image');
  const documents = assets.filter((a) => a.kind === 'document');
  const variantName = (variantId: number | null) => variants.find((v) => v.id === variantId)?.name;

  useEffect(() => {
    if (data && board && !dirty) {
      setForm(boardFormFrom(listing, board.listing_slug || board.board.toLowerCase()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, board]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['listing', boardId] });
    queryClient.invalidateQueries({ queryKey: ['device-board', boardId] });
    queryClient.invalidateQueries({ queryKey: ['device-boards'] });
  };

  const saveBoard = useMutation({
    mutationFn: (f: BoardForm) => updateListing(boardId, {
      slug: f.slug.trim(),
      headline: f.headline.trim() || null,
      description: f.description || null,
      specs: cleanSpecs(f.specs),
    }),
    onSuccess: () => { setDirty(false); setError(null); setNotice('Saved.'); invalidate(); },
    onError: (err) => setError(errorText(err, 'Failed to save')),
  });

  const publish = useMutation({
    mutationFn: (published: boolean) => publishListing(boardId, published),
    onSuccess: (l) => { setError(null); setNotice(l.published ? 'Listing is live.' : 'Listing taken down.'); invalidate(); },
    onError: (err) => setError(errorText(err, 'Failed to change publish state')),
  });

  const setHero = useMutation<BoardListing | BoardVariant, unknown, { assetId: number; variantId: number | null }>({
    // Shared image → board hero; product image → that product's hero.
    mutationFn: ({ assetId, variantId }) => (variantId
      ? updateVariant(boardId, variantId, { hero_asset_id: assetId })
      : updateListing(boardId, { hero_asset_id: assetId })),
    onSuccess: invalidate,
    onError: (err) => setError(errorText(err, 'Failed to set hero image')),
  });

  const removeAsset = useMutation({
    mutationFn: (assetId: number) => deleteAsset(boardId, assetId),
    onSuccess: invalidate,
    onError: (err) => setError(errorText(err, 'Failed to delete file')),
  });

  const patchAsset = useMutation({
    mutationFn: ({ assetId, ...data }: { assetId: number; title?: string | null; variant_id?: number | null }) => updateAsset(boardId, assetId, data),
    onSuccess: invalidate,
    onError: (err) => setError(errorText(err, 'Failed to update file')),
  });

  const reorder = useMutation({
    mutationFn: (ids: number[]) => reorderAssets(boardId, ids),
    onSuccess: invalidate,
    onError: (err) => setError(errorText(err, 'Failed to reorder')),
  });

  const saveProduct = useMutation({
    mutationFn: (input: VariantInput) => (editingProduct
      ? updateVariant(boardId, editingProduct.id, input)
      : createVariant(boardId, input)),
    onSuccess: () => { setProductForm(null); setEditingProduct(null); setError(null); invalidate(); },
    onError: (err) => setError(errorText(err, 'Failed to save product')),
  });

  const reorderProducts = useMutation({
    mutationFn: (ids: number[]) => reorderVariants(boardId, ids),
    onSuccess: invalidate,
    onError: (err) => setError(errorText(err, 'Failed to reorder products')),
  });

  const removeProduct = useMutation({
    mutationFn: (variantId: number) => deleteVariant(boardId, variantId),
    onSuccess: invalidate,
    onError: (err) => setError(errorText(err, 'Failed to delete product')),
  });

  const onFiles = async (files: FileList | null) => {
    if (!files || !files.length) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        // eslint-disable-next-line no-await-in-loop
        await uploadAsset(boardId, file, { variantId: uploadTarget ? Number(uploadTarget) : null });
      }
      invalidate();
    } catch (err) {
      setError(errorText(err, 'Upload failed'));
    } finally {
      setUploading(false);
      if (imageInput.current) imageInput.current.value = '';
      if (documentInput.current) documentInput.current.value = '';
    }
  };

  const moveAsset = (list: BoardAsset[], index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    const ids = list.map((a) => a.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorder.mutate(ids);
  };

  const moveProduct = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= variants.length) return;
    const ids = variants.map((v) => v.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderProducts.mutate(ids);
  };

  if (isLoading || boardLoading || !form) return <PageContainer><LoadingText>Loading listing...</LoadingText></PageContainer>;
  if (isError || !board) {
    return (
      <PageContainer>
        <EmptyState><p>Board not found.</p><p><Link to={`/hardware-providers/${manufacturerId}`}>Back</Link></p></EmptyState>
      </PageContainer>
    );
  }

  const live = listing?.published === true;
  const publicPath = manufacturer && listing ? publicListingPath(manufacturer.slug, listing.slug) : null;
  const missing: string[] = [];
  if (!form.headline.trim() && !listing?.headline) missing.push('a headline');
  if (images.length === 0) missing.push('at least one image');

  // Group assets: shared first, then per product in product order.
  const groups: { key: string; label: string; variantId: number | null }[] = [
    { key: 'shared', label: 'Shared — whole board', variantId: null },
    ...variants.map((v) => ({ key: String(v.id), label: `${v.name} (${v.sku})`, variantId: v.id })),
  ];
  const heroFor = (a: BoardAsset) => (a.variant_id
    ? variants.find((v) => v.id === a.variant_id)?.hero_asset_id === a.id
    : listing?.hero_asset_id === a.id);

  const renderAssignSelect = (a: BoardAsset) => (
    <Select
      value={a.variant_id ?? ''}
      onChange={(e) => patchAsset.mutate({ assetId: a.id, variant_id: e.target.value ? Number(e.target.value) : null })}
      title="Which product this file belongs to"
    >
      <option value="">Shared — whole board</option>
      {variants.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.sku})</option>)}
    </Select>
  );

  const uploadTargetSelect = (
    <Select value={uploadTarget} onChange={(e) => setUploadTarget(e.target.value)} title="Attach uploads to">
      <option value="">Upload as shared (whole board)</option>
      {variants.map((v) => <option key={v.id} value={v.id}>Upload for {v.name} ({v.sku})</option>)}
    </Select>
  );

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <BackLink to={`/hardware-providers/${manufacturerId}?tab=boards`}>← {manufacturer?.name || 'Hardware provider'}</BackLink>
          <PageTitle>{boardLabel(board)} listing</PageTitle>
          <Caption>{board.manufacturer} · {board.board}</Caption>
        </div>
        <Row gap={2}>
          {live && publicPath && (
            <PublicLink href={publicPath} target="_blank" rel="noreferrer">
              <IconExternalLink size={16} /> View public page
            </PublicLink>
          )}
          <Button
            variant={live ? 'secondary' : 'primary'}
            disabled={publish.isPending || (!live && missing.length > 0)}
            title={!live && missing.length ? `Add ${missing.join(' and ')} first` : undefined}
            onClick={() => {
              if (live && !window.confirm('Take this listing off the public site?')) return;
              publish.mutate(!live);
            }}
          >
            {publish.isPending ? 'Working...' : live ? 'Unpublish' : 'Publish'}
          </Button>
        </Row>
      </PageHeader>

      <StatusBar live={live}>
        <div>
          <Row gap={2} align="center">
            <StatusChip variant={live ? 'positive' : 'neutral'}>{live ? 'published' : 'draft'}</StatusChip>
            {live && listing?.published_at && <Caption>live since {new Date(listing.published_at).toLocaleDateString()}</Caption>}
          </Row>
          <Caption>
            {live
              ? 'Changes show on the public page as soon as they are saved.'
              : missing.length
                ? `Before publishing, add ${missing.join(' and ')}.`
                : 'Ready to publish.'}
          </Caption>
        </div>
        {publicPath && <Caption>Public address: <code>{publicPath}</code></Caption>}
      </StatusBar>

      {error && <ErrorAlert>{error}</ErrorAlert>}
      {notice && !error && <Caption>{notice}</Caption>}

      <SegmentedTabs
        tabs={[
          { id: 'board', label: 'Board page' },
          { id: 'products', label: `Products / SKUs (${variants.length})` },
          { id: 'images', label: `Images (${images.length})` },
          { id: 'documents', label: `Documents (${documents.length})` },
        ]}
        activeId={tab}
        onChange={(t) => setTab(t as Tab)}
      />

      {tab === 'board' && (
        <form onSubmit={(e) => { e.preventDefault(); saveBoard.mutate(form); }}>
          <MutedText>
            The board page is what every product built on this board shares: the platform
            story and the electronics specs. Housing-specific details go on each product.
          </MutedText>
          <FieldGrid>
            <FormGroup>
              <Label>Headline</Label>
              <Input
                value={form.headline}
                onChange={(e) => { setForm({ ...form, headline: e.target.value }); setDirty(true); }}
                placeholder="Single-door cellular access controller"
                maxLength={200}
              />
            </FormGroup>
            <FormGroup>
              <Label>Public address</Label>
              <Input
                value={form.slug}
                onChange={(e) => { setForm({ ...form, slug: e.target.value }); setDirty(true); }}
                pattern="[a-z0-9]+(-[a-z0-9]+)*"
                required
              />
              <HelperText>Lowercase letters, digits, hyphens. Unique among your boards.</HelperText>
            </FormGroup>
          </FieldGrid>

          <FormGroup>
            <Label>Description</Label>
            <TextArea
              rows={10}
              value={form.description}
              onChange={(e) => { setForm({ ...form, description: e.target.value }); setDirty(true); }}
              placeholder="What the platform is and what it's for. Blank lines start new paragraphs."
            />
          </FormGroup>

          <SpacedSectionTitle>Board specifications</SpacedSectionTitle>
          <MutedText>Shown on every product page, before the product's own rows. Grouped by the optional group name.</MutedText>
          <SpecEditor specs={form.specs} onChange={(specs) => { setForm({ ...form, specs }); setDirty(true); }} />

          <Row gap={2} style={{ marginTop: 24 }}>
            <Button variant="primary" type="submit" disabled={saveBoard.isPending || !dirty}>
              {saveBoard.isPending ? 'Saving...' : 'Save'}
            </Button>
            {dirty && <Caption>Unsaved changes</Caption>}
          </Row>
        </form>
      )}

      {tab === 'products' && (
        <>
          <MutedText>
            Products are the SKUs built on this board — same electronics, different housing or
            application (SB-01, SB-02, SB-03…). Each gets its own page with its own images,
            documents and spec rows on top of the board's.
          </MutedText>
          <Row gap={2} style={{ margin: '12px 0' }}>
            <Button variant="primary" onClick={() => { setEditingProduct(null); setProductForm(emptyProduct()); }}>
              <IconPlus size={16} /> Add product
            </Button>
          </Row>

          {productForm && (
            <DetailCard>
              <form onSubmit={(e) => {
                e.preventDefault();
                saveProduct.mutate({
                  name: productForm.name,
                  sku: productForm.sku,
                  ...(productForm.slug.trim() ? { slug: productForm.slug.trim() } : {}),
                  status: productForm.status,
                  headline: productForm.headline.trim() || null,
                  description: productForm.description || null,
                  specs: cleanSpecs(productForm.specs),
                });
              }}>
                <FieldGrid>
                  <FormGroup>
                    <Label>Name</Label>
                    <Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} placeholder="SB-01" required maxLength={100} />
                  </FormGroup>
                  <FormGroup>
                    <Label>SKU</Label>
                    <Input value={productForm.sku} onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })} placeholder="SB-01" required maxLength={60} />
                    <HelperText>Letters, digits, dots, underscores, hyphens. Unique on this board.</HelperText>
                  </FormGroup>
                </FieldGrid>
                <FieldGrid>
                  <FormGroup>
                    <Label>Status</Label>
                    <Select value={productForm.status} onChange={(e) => setProductForm({ ...productForm, status: e.target.value as VariantStatus })}>
                      {(Object.keys(VARIANT_STATUS_LABELS) as VariantStatus[]).map((s) => <option key={s} value={s}>{VARIANT_STATUS_LABELS[s]}</option>)}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Public address</Label>
                    <Input value={productForm.slug} onChange={(e) => setProductForm({ ...productForm, slug: e.target.value })} placeholder="derived from name" pattern="[a-z0-9]+(-[a-z0-9]+)*" />
                  </FormGroup>
                </FieldGrid>
                <FormGroup>
                  <Label>Headline</Label>
                  <Input value={productForm.headline} onChange={(e) => setProductForm({ ...productForm, headline: e.target.value })} placeholder="Surface-mount housing for interior doors" maxLength={200} />
                </FormGroup>
                <FormGroup>
                  <Label>Description</Label>
                  <TextArea rows={5} value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} placeholder="What makes this product different: housing, mounting, use case, what's in the box." />
                </FormGroup>
                <Label>Product specifications</Label>
                <HelperText>Added after the board's rows on this product's page — housing, dimensions, finish, IP rating.</HelperText>
                <SpecEditor specs={productForm.specs} onChange={(specs) => setProductForm({ ...productForm, specs })} />
                <Row gap={2} style={{ marginTop: 16 }}>
                  <Button variant="primary" type="submit" disabled={saveProduct.isPending}>{editingProduct ? 'Save product' : 'Add product'}</Button>
                  <Button variant="secondary" type="button" onClick={() => { setProductForm(null); setEditingProduct(null); }}>Cancel</Button>
                </Row>
              </form>
            </DetailCard>
          )}

          {variants.length === 0 ? (
            <EmptyState><p>No products yet. Add one per SKU built on this board.</p></EmptyState>
          ) : (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr><Th>Product</Th><Th>SKU</Th><Th>Status</Th><Th>Images</Th><Th>Documents</Th><Th>Specs</Th><Th></Th></tr>
                </thead>
                <tbody>
                  {variants.map((v, i) => (
                    <tr key={v.id}>
                      <Td>
                        <Stack gap={1}>
                          <strong>{v.name}</strong>
                          {v.headline && <Caption>{v.headline}</Caption>}
                        </Stack>
                      </Td>
                      <Td><code>{v.sku}</code></Td>
                      <Td><StatusChip variant={v.status === 'available' ? 'positive' : v.status === 'coming-soon' ? 'info' : 'neutral'}>{VARIANT_STATUS_LABELS[v.status]}</StatusChip></Td>
                      <Td>{v.image_count ?? 0}{v.hero_asset_id ? '' : <Caption> · no hero</Caption>}</Td>
                      <Td>{v.document_count ?? 0}</Td>
                      <Td>{v.specs?.length ?? 0}</Td>
                      <Td>
                        <Row gap={1}>
                          <IconButton onClick={() => moveProduct(i, -1)} title="Move up" disabled={i === 0}><IconArrowUp size={14} /></IconButton>
                          <IconButton onClick={() => moveProduct(i, 1)} title="Move down" disabled={i === variants.length - 1}><IconArrowDown size={14} /></IconButton>
                          <Button size="sm" variant="secondary" onClick={() => { setEditingProduct(v); setProductForm(productFormFrom(v)); }}>Edit</Button>
                          <Button size="sm" variant="danger" onClick={() => { if (window.confirm(`Delete ${v.name}? Its files stay on the board as shared.`)) removeProduct.mutate(v.id); }}>Delete</Button>
                        </Row>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}
        </>
      )}

      {tab === 'images' && (
        <>
          <Row gap={2} align="center" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
            {uploadTargetSelect}
            <Button variant="primary" disabled={uploading} onClick={() => imageInput.current?.click()}>
              {uploading ? 'Uploading...' : 'Upload images'}
            </Button>
            <HiddenFile ref={imageInput} type="file" accept={IMAGE_ACCEPT} multiple onChange={(e) => onFiles(e.target.files)} />
          </Row>
          <Caption>PNG, JPG or WebP, up to 5 MB each. Shared images show on the board page; a product's images show on its page. The starred image is that group's hero.</Caption>
          {images.length === 0 ? (
            <EmptyState><p>No images yet.</p></EmptyState>
          ) : groups.map((g) => {
            const list = images.filter((a) => (a.variant_id ?? null) === g.variantId);
            if (!list.length) return null;
            return (
              <div key={g.key}>
                <GroupHeading>{g.label}</GroupHeading>
                <Gallery>
                  {list.map((a, i) => {
                    const isHero = heroFor(a);
                    return (
                      <Tile key={a.id} hero={isHero}>
                        <AssetImage boardId={boardId} assetId={a.id} published={live} alt={a.title || a.file_name} />
                        <Input
                          key={`${a.id}-${a.title || ''}`}
                          defaultValue={a.title || ''}
                          placeholder="Caption"
                          onBlur={(e) => { if ((e.target.value || null) !== (a.title || null)) patchAsset.mutate({ assetId: a.id, title: e.target.value || null }); }}
                        />
                        {renderAssignSelect(a)}
                        <Caption>{a.file_name} · {formatBytes(a.size_bytes)}</Caption>
                        <TileActions>
                          <Row gap={1}>
                            <IconButton onClick={() => moveAsset(list, i, -1)} title="Move earlier" disabled={i === 0}><IconArrowUp size={14} /></IconButton>
                            <IconButton onClick={() => moveAsset(list, i, 1)} title="Move later" disabled={i === list.length - 1}><IconArrowDown size={14} /></IconButton>
                            <IconButton onClick={() => setHero.mutate({ assetId: a.id, variantId: a.variant_id })} title={isHero ? 'Hero image' : 'Make hero image'}>
                              {isHero ? <IconStarFilled size={14} /> : <IconStar size={14} />}
                            </IconButton>
                          </Row>
                          <IconButton onClick={() => { if (window.confirm('Delete this image?')) removeAsset.mutate(a.id); }} title="Delete"><IconTrash size={14} /></IconButton>
                        </TileActions>
                      </Tile>
                    );
                  })}
                </Gallery>
              </div>
            );
          })}
        </>
      )}

      {tab === 'documents' && (
        <>
          <Row gap={2} align="center" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
            {uploadTargetSelect}
            <Button variant="primary" disabled={uploading} onClick={() => documentInput.current?.click()}>
              {uploading ? 'Uploading...' : 'Upload documents'}
            </Button>
            <HiddenFile ref={documentInput} type="file" accept={DOCUMENT_ACCEPT} multiple onChange={(e) => onFiles(e.target.files)} />
          </Row>
          <Caption>PDF, up to 15 MB each. Datasheets, install guides, certifications. Shared documents show on every page; a product's only on its own.</Caption>
          {documents.length === 0 ? (
            <EmptyState><p>No documents yet.</p></EmptyState>
          ) : (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr><Th>Title</Th><Th>File</Th><Th>Belongs to</Th><Th></Th></tr>
                </thead>
                <tbody>
                  {documents.map((d, i) => (
                    <tr key={d.id}>
                      <Td>
                        <Input
                          key={`${d.id}-${d.title || ''}`}
                          defaultValue={d.title || ''}
                          placeholder={d.file_name}
                          onBlur={(e) => { if ((e.target.value || null) !== (d.title || null)) patchAsset.mutate({ assetId: d.id, title: e.target.value || null }); }}
                        />
                      </Td>
                      <Td><Caption>{d.file_name} · {formatBytes(d.size_bytes)}</Caption></Td>
                      <Td>{renderAssignSelect(d)}</Td>
                      <Td>
                        <Row gap={1}>
                          <IconButton onClick={() => moveAsset(documents, i, -1)} title="Move up" disabled={i === 0}><IconArrowUp size={14} /></IconButton>
                          <IconButton onClick={() => moveAsset(documents, i, 1)} title="Move down" disabled={i === documents.length - 1}><IconArrowDown size={14} /></IconButton>
                          <IconButton onClick={() => { if (window.confirm('Delete this document?')) removeAsset.mutate(d.id); }} title="Delete"><IconTrash size={14} /></IconButton>
                        </Row>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}
          {documents.some((d) => d.variant_id && !variantName(d.variant_id)) && (
            <Caption>Some documents point at a product that no longer exists; reassign them.</Caption>
          )}
        </>
      )}
    </PageContainer>
  );
}

export default BoardListingEditor;
